/**
 * Zustand App Store - Shared brain for all pages
 *
 * This store holds UI state that multiple pages need to access.
 * Like having a shared notebook that ALL pages can read and write to.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Client, UserProfile, Call } from '../types/api'
import api, { getAccessToken } from '../services/api'
import { claimReferralMilestone, getReferralStats } from '../services/waitlistService'

// ============================================
// App Mode (Consumer vs Business)
// ============================================

export type AppMode = 'individual' | 'business'
export type DevModeOverride = 'individual' | 'business' | 'both' | null

interface AppModeSlice {
  mode: AppMode
  setMode: (mode: AppMode) => void
  toggleMode: () => void
  // Dev mode override from URL params
  devModeOverride: DevModeOverride
  setDevModeOverride: (override: DevModeOverride) => void
}

// ============================================
// Selected Account (for B2B multi-tenant)
// ============================================

interface AccountSlice {
  selectedAccountId: string | null
  selectedAccount: Client | null
  setSelectedAccount: (account: Client | null) => void
  clearSelectedAccount: () => void
}

// ============================================
// UI State (sidebar, modals, etc.)
// ============================================

interface UISlice {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  toggleSidebar: () => void

  // Modal states
  activeModal: string | null
  openModal: (modalId: string) => void
  closeModal: () => void

  // Toast notifications
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void

  // Demo mode (separate for individual and business)
  individualDemoMode: boolean
  setIndividualDemoMode: (enabled: boolean) => void
  businessDemoMode: boolean
  setBusinessDemoMode: (enabled: boolean) => void
}

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

// ============================================
// Consumer Mode State
// ============================================

interface ConsumerSlice {
  // Current task/conversation
  currentTask: string | null
  setCurrentTask: (task: string | null) => void

  // Research results (cached)
  researchCache: Record<string, ResearchCacheEntry>
  setResearchCache: (query: string, data: ResearchCacheEntry) => void
  clearResearchCache: () => void

  // Active call
  activeCallId: string | null
  setActiveCallId: (callId: string | null) => void
}

interface ResearchCacheEntry {
  data: {
    name: string
    phone: string
    hours: string
    notes: string
  }
  timestamp: number
}

// ============================================
// Business Mode State
// ============================================

interface BusinessSlice {
  // Live monitoring
  monitoringCallId: string | null
  setMonitoringCallId: (callId: string | null) => void

  // Dashboard filters
  dashboardDateRange: 'today' | 'week' | 'month' | 'all'
  setDashboardDateRange: (range: 'today' | 'week' | 'month' | 'all') => void

  // Knowledge management
  selectedDocumentId: string | null
  setSelectedDocumentId: (docId: string | null) => void
}

// ============================================
// Subscription State
// ============================================

export interface Subscription {
  planId: 'free' | 'pro' | 'pro_plus' | 'business' | 'enterprise'
  planName: string
  status: 'active' | 'canceled' | 'past_due'
  billingCycle: 'monthly' | 'annual'
  currentPeriodEnd: number
  cancelAtPeriodEnd: boolean
}

interface SubscriptionSlice {
  subscription: Subscription | null
  subscriptionLoading: boolean
  setSubscription: (subscription: Subscription | null) => void
  fetchSubscription: () => Promise<void>
}

// ============================================
// User State (for consumer mode)
// ============================================

interface UserSlice {
  user: UserProfile | null
  setUser: (user: UserProfile | null) => void
  updateUserField: <K extends keyof UserProfile>(field: K, value: UserProfile[K]) => void
  isAuthenticated: boolean
}

// ============================================
// Pending Tasks State
// ============================================

interface PendingTasksSlice {
  pendingTaskCount: number
  pendingTasksLoading: boolean
  setPendingTaskCount: (count: number) => void
  fetchPendingTaskCount: () => Promise<void>
}

// ============================================
// Referral State
// ============================================

export interface ReferralStats {
  referralCode: string
  referralCount: number
  totalCreditsEarned: number
  currentCredits: number
  spotsJumped: number
  waitlistPosition: number | null
  waitlistTotal: number | null
  milestones: {
    type: string
    unlocked: boolean
    rewardClaimed: boolean
  }[]
}

interface ReferralSlice {
  referralStats: ReferralStats | null
  referralLoading: boolean
  setReferralStats: (stats: ReferralStats | null) => void
  fetchReferralStats: () => Promise<void>
  claimMilestone: (milestoneType: string) => Promise<boolean>
}

// ============================================
// Combined Store Type
// ============================================

type AppStore = AppModeSlice &
  AccountSlice &
  UISlice &
  ConsumerSlice &
  BusinessSlice &
  SubscriptionSlice &
  UserSlice &
  ReferralSlice &
  PendingTasksSlice

// ============================================
// Store Implementation
// ============================================

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // ----- App Mode -----
      mode: 'individual',
      setMode: (mode) => set({ mode }),
      toggleMode: () =>
        set((state) => ({
          mode: state.mode === 'individual' ? 'business' : 'individual',
        })),
      devModeOverride: null as DevModeOverride,
      setDevModeOverride: (override) => set({ devModeOverride: override }),

      // ----- Selected Account -----
      selectedAccountId: null as string | null,
      selectedAccount: null as Client | null,
      setSelectedAccount: (account) =>
        set({
          selectedAccount: account,
          selectedAccountId: account?.id ?? null,
        }),
      clearSelectedAccount: () =>
        set({ selectedAccount: null, selectedAccountId: null }),

      // ----- UI State -----
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      activeModal: null as string | null,
      openModal: (modalId) => set({ activeModal: modalId }),
      closeModal: () => set({ activeModal: null }),

      toasts: [] as Toast[],
      addToast: (toast) =>
        set((state) => ({
          toasts: [
            ...state.toasts,
            { ...toast, id: `toast-${Date.now()}-${Math.random()}` },
          ],
        })),
      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      individualDemoMode: true, // Default to true (show demo data in individual mode)
      setIndividualDemoMode: (enabled) => set({ individualDemoMode: enabled }),
      businessDemoMode: true, // Default to true (show demo data in business mode)
      setBusinessDemoMode: (enabled) => set({ businessDemoMode: enabled }),

      // ----- Consumer Mode -----
      currentTask: null as string | null,
      setCurrentTask: (task) => set({ currentTask: task }),

      researchCache: {},
      setResearchCache: (query, data) =>
        set((state) => ({
          researchCache: {
            ...state.researchCache,
            [query.toLowerCase()]: data,
          },
        })),
      clearResearchCache: () => set({ researchCache: {} }),

      activeCallId: null as string | null,
      setActiveCallId: (callId) => set({ activeCallId: callId }),

      // ----- Business Mode -----
      monitoringCallId: null as string | null,
      setMonitoringCallId: (callId) => set({ monitoringCallId: callId }),

      dashboardDateRange: 'today',
      setDashboardDateRange: (range) => set({ dashboardDateRange: range }),

      selectedDocumentId: null as string | null,
      setSelectedDocumentId: (docId) => set({ selectedDocumentId: docId }),

      // ----- Subscription State -----
      subscription: null as Subscription | null,
      subscriptionLoading: false,
      setSubscription: (subscription) => set({ subscription }),
      fetchSubscription: async () => {
        set({ subscriptionLoading: true })
        try {
          const data = await api.get<{ planId?: Subscription['planId']; planName?: string; periodEnd?: number }>('/api/billing/usage')
          set({
            subscription: {
              planId: data.planId || 'free',
              planName: data.planName || 'Free',
              status: 'active',
              billingCycle: 'monthly',
              currentPeriodEnd: data.periodEnd || 0,
              cancelAtPeriodEnd: false,
            },
            subscriptionLoading: false,
          })
        } catch {
          // Default to free plan on error
          set({
            subscription: {
              planId: 'free',
              planName: 'Free',
              status: 'active',
              billingCycle: 'monthly',
              currentPeriodEnd: 0,
              cancelAtPeriodEnd: false,
            },
            subscriptionLoading: false,
          })
        }
      },

      // ----- User State -----
      user: null as UserProfile | null,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      updateUserField: (field, value) =>
        set((state) => ({
          user: state.user ? { ...state.user, [field]: value } : null,
        })),
      isAuthenticated: false,

      // ----- Pending Tasks State -----
      pendingTaskCount: 0,
      pendingTasksLoading: false,
      setPendingTaskCount: (count) => set({ pendingTaskCount: count }),
      fetchPendingTaskCount: async () => {
        set({ pendingTasksLoading: true })
        try {
          const token = getAccessToken()
          if (!token) {
            set({ pendingTasksLoading: false })
            return
          }

          const data = await api.get<{ tasks?: Array<{ status: string }> }>('/api/user/tasks')
          const tasks = data.tasks || []
          // Count tasks with needs_input status
          const needsInputCount = tasks.filter(
            (t: { status: string }) => t.status === 'needs_input'
          ).length
          set({
            pendingTaskCount: needsInputCount,
            pendingTasksLoading: false,
          })
        } catch {
          set({ pendingTasksLoading: false })
        }
      },

      // ----- Referral State -----
      referralStats: null as ReferralStats | null,
      referralLoading: false,
      setReferralStats: (stats) => set({ referralStats: stats }),
      fetchReferralStats: async () => {
        set({ referralLoading: true })
        try {
          const token = getAccessToken()
          if (!token) {
            set({ referralLoading: false })
            return
          }

          const data = await getReferralStats() as {
            referral_code: string
            referral_count: number
            total_credits_earned: number
            current_credits: number
            spots_jumped?: number
            waitlist_position: number | null
            waitlist_total: number | null
            milestones?: ReferralStats['milestones']
          }
          set({
            referralStats: {
              referralCode: data.referral_code,
              referralCount: data.referral_count,
              totalCreditsEarned: data.total_credits_earned,
              currentCredits: data.current_credits,
              spotsJumped: data.spots_jumped || 0,
              waitlistPosition: data.waitlist_position,
              waitlistTotal: data.waitlist_total,
              milestones: data.milestones || [],
            },
            referralLoading: false,
          })
        } catch {
          set({ referralLoading: false })
        }
      },
      claimMilestone: async (milestoneType: string) => {
        try {
          const token = getAccessToken()
          if (!token) return false

          await claimReferralMilestone(milestoneType)
          // Refetch stats to get updated state
          await get().fetchReferralStats()
          return true
        } catch {
          return false
        }
      },
    }),
    {
      name: 'reeva-app-store',
      // Only persist certain fields
      partialize: (state) => ({
        mode: state.mode,
        selectedAccountId: state.selectedAccountId,
        dashboardDateRange: state.dashboardDateRange,
        researchCache: state.researchCache,
        individualDemoMode: state.individualDemoMode,
        businessDemoMode: state.businessDemoMode,
        monitoringCallId: state.monitoringCallId,
      }),
    }
  )
)

// ============================================
// Selector Hooks (for performance)
// ============================================

// Use these to subscribe to specific slices only
// Prevents re-renders when unrelated state changes

export const useAppMode = () => useAppStore((state) => state.mode)
export const useSetAppMode = () => useAppStore((state) => state.setMode)

export const useSelectedAccount = () =>
  useAppStore((state) => state.selectedAccount)
export const useSetSelectedAccount = () =>
  useAppStore((state) => state.setSelectedAccount)

export const useSidebarOpen = () => useAppStore((state) => state.sidebarOpen)
export const useToggleSidebar = () =>
  useAppStore((state) => state.toggleSidebar)

export const useActiveModal = () => useAppStore((state) => state.activeModal)
export const useModalActions = () =>
  useAppStore((state) => ({
    openModal: state.openModal,
    closeModal: state.closeModal,
  }))

export const useToasts = () => useAppStore((state) => state.toasts)
export const useToastActions = () =>
  useAppStore((state) => ({
    addToast: state.addToast,
    removeToast: state.removeToast,
  }))

export const useUser = () => useAppStore((state) => state.user)
export const useIsAuthenticated = () =>
  useAppStore((state) => state.isAuthenticated)

export const useSubscription = () => useAppStore((state) => state.subscription)
export const useSubscriptionLoading = () =>
  useAppStore((state) => state.subscriptionLoading)
export const useFetchSubscription = () =>
  useAppStore((state) => state.fetchSubscription)

// Returns true if user has Business or Enterprise plan (can access Business mode)
// Also returns true if devModeOverride is 'both' or 'business'
export const useHasBusinessAccess = () =>
  useAppStore((state) => {
    // Dev mode override takes precedence
    if (state.devModeOverride === 'both' || state.devModeOverride === 'business') {
      return true
    }
    const planId = state.subscription?.planId
    return planId === 'business' || planId === 'enterprise'
  })

// Individual demo mode selectors
export const useIndividualDemoMode = () => useAppStore((state) => state.individualDemoMode)
export const useSetIndividualDemoMode = () => useAppStore((state) => state.setIndividualDemoMode)

// Dev mode selectors
export const useDevModeOverride = () => useAppStore((state) => state.devModeOverride)
export const useSetDevModeOverride = () => useAppStore((state) => state.setDevModeOverride)

// Demo mode selectors
// Legacy aliases for personal demo mode (deprecated - use Individual)
export const usePersonalDemoMode = () => useAppStore((state) => state.individualDemoMode)
export const useSetPersonalDemoMode = () => useAppStore((state) => state.setIndividualDemoMode)

// Business demo mode selectors
export const useBusinessDemoMode = () => useAppStore((state) => state.businessDemoMode)
export const useSetBusinessDemoMode = () => useAppStore((state) => state.setBusinessDemoMode)

// Referral selectors
export const useReferralStats = () => useAppStore((state) => state.referralStats)
export const useReferralLoading = () => useAppStore((state) => state.referralLoading)
export const useFetchReferralStats = () => useAppStore((state) => state.fetchReferralStats)
export const useClaimMilestone = () => useAppStore((state) => state.claimMilestone)
export const useReferralCode = () => useAppStore((state) => state.referralStats?.referralCode)
export const useReferralCount = () => useAppStore((state) => state.referralStats?.referralCount ?? 0)
export const useTaskCredits = () => useAppStore((state) => state.referralStats?.currentCredits ?? 0)

// Pending Tasks selectors
export const usePendingTaskCount = () => useAppStore((state) => state.pendingTaskCount)
export const usePendingTasksLoading = () => useAppStore((state) => state.pendingTasksLoading)
export const useFetchPendingTaskCount = () => useAppStore((state) => state.fetchPendingTaskCount)
